import React, { useState } from 'react';
import { Container, Grid, Box, Typography, useTheme, useMediaQuery, Button, Chip, Stack, Avatar, Card } from '@mui/material';
import { motion } from 'framer-motion';
import { serviceCategories, processSteps, featureItems } from './data';
import { ServiceCard } from './ServiceCard';
import { ProcessStep } from './ProcessStep';
import { FeatureItem } from './FeatureItem';
import { CTASection } from './CTASection';
import Particles from 'react-tsparticles';
import { loadFull } from 'tsparticles';
import { fadeInUp, getTheme } from '../../theme/theme';
import { ArrowRightAlt, BusinessCenter } from '@mui/icons-material';
import { Parallax } from 'react-scroll-parallax';
import heroImage from '../../assets/images/services.png';
import { useNavigate } from 'react-router-dom';
import ServicesHero from './services-hero';

export const Services = ({ mode }) => {
  const theme = getTheme(mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  const navigate = useNavigate();
  const [particlesLoaded, setParticlesLoaded] = useState(false);

  const particlesInit = async (engine) => {
    await loadFull(engine);
  };

  const particlesLoaded$ = async (container) => {
    setParticlesLoaded(true);
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: -60 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: "easeOut", delay: 0.2 } }
  };

  return (
    <Box sx={{
      bgcolor: mode === 'light' ? 'background.default' : 'background.default',
      color: 'text.primary',
      position: 'relative',
      overflow: 'hidden',
      pb: 8
    }}>
      <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0 }}>
        <Particles
          id="tsparticles"
          init={particlesInit}
          loaded={particlesLoaded$}
          options={{
            fullScreen: { enable: false },
            background: {
              color: {
                value: 'transparent',
              },
            },
            fpsLimit: 60,
            particles: {
              color: {
                value: mode === 'light' ? '#6a1bff' : '#00e5ff',
              },
              links: {
                color: mode === 'light' ? '#6a1bff' : '#00e5ff',
                distance: 150,
                enable: true,
                opacity: 0.3,
                width: 1,
              },
              collisions: {
                enable: true,
              },
              move: {
                direction: 'none',
                enable: true,
                outModes: {
                  default: 'bounce',
                },
                random: false,
                speed: 0.5,
                straight: false,
              },
              number: {
                density: {
                  enable: true,
                  area: 800,
                },
                value: 40,
              },
              opacity: {
                value: 0.3,
              },
              shape: {
                type: 'circle',
              },
              size: {
                value: { min: 1, max: 3 },
              },
            },
            detectRetina: true,
          }}
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: 0
          }}
        />
      </Box>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <ServicesHero mode={mode} />



        
        {/* Header Section */}
        <Box sx={{
          py: { xs: 6, md: 10 },
          textAlign: 'center'
        }}>
          {/* Service Cards */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gap: { xs: 4, md: 5 },
            mb: { xs: 8, md: 12 }
          }}>
            {serviceCategories.map((category, index) => (
              <ServiceCard
                key={index}
                title={category.title}
                icon={category.icon}
                description={category.description}
                services={category.services}
                image={category.image}
                color={category.color}
                index={index}
              />
            ))}
          </Box>
        </Box>

        {/* Process Section */}
        <Box
          sx={{
            py: { xs: 6, md: 10 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '100vw',
              height: '100%',
              bgcolor: mode === 'light' ? 'rgba(106, 27, 255, 0.05)' : 'rgba(0, 0, 0, 0.2)',
              zIndex: -1
            }
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1,
                display: 'block',
                textAlign: 'center'
              }}
            >
              OUR PROCESS
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textAlign: 'center'
              }}
            >
              How We Work
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 'md',
                mx: 'auto',
                textAlign: 'center',
                mb: 8
              }}
            >
              We follow a proven methodology to ensure consistent results
              and successful project delivery for our clients.
            </Typography>
          </motion.div>

          {isMobile ? (
            <Box>
              {processSteps.map((step, index) => (
                <ProcessStep
                  key={index}
                  title={step.title}
                  description={step.description}
                  icon={step.icon}
                  color={step.color}
                  step={step.step}
                  index={index}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{
              py: { xs: 6, md: 10 },
              textAlign: 'center'
            }}>
              {/* Service Cards */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: { xs: 4, md: 5 },
                // mb: { xs: 8, md: 12 }
              }}>
                {processSteps.map((step, index) => (

                  <ProcessStep
                    title={step.title}
                    description={step.description}
                    icon={step.icon}
                    color={step.color}
                    step={step.step}
                    index={index}
                  />
                ))}
              </Box>
            </Box>





          )}
        </Box>

        {/* Features Section */}
        <Box sx={{ py: { xs: 6, md: 10 } }}>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1,
                display: 'block',
                textAlign: 'center'
              }}
            >
              OUR FEATURES
            </Typography>

            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                textAlign: 'center'
              }}
            >
              Why Choose Us
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                maxWidth: 'md',
                mx: 'auto',
                textAlign: 'center',
                mb: 8
              }}
            >
              We combine technical expertise with business insight
              to deliver solutions that drive real results.
            </Typography>
          </motion.div>

          <Box sx={{
            py: { xs: 6, md: 10 },
            textAlign: 'center',
          }}>
            {/* Service Cards */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)'
              },
              gap: { xs: 4, md: 5 },

              // mb: { xs: 8, md: 12 }
            }}>
              {featureItems.map((feature, index) => (
                <Box sx={{ mb: 10 }} key={index}>
                  <FeatureItem
                    title={feature.title}
                    description={feature.description}
                    icon={feature.icon}
                    color={feature.color}
                    delay={index * 0.1}
                  /></Box>))}
            </Box>
          </Box>





        </Box>

        {/* CTA Section */}
        <CTASection />
      </Container>
    </Box>
  );
};